/**
 * Which suggestion to offer next.
 *
 * Extracted because it was wrong and nothing could have caught it. Task 0044
 * said the picker's rule "deserves extraction into a testable module the way
 * `plot.ts` was"; this is that, prompted by the rule turning out to jam.
 *
 * ## What jammed
 *
 * `keep()` deliberately does not retire a candidate — `candidate.ts` is right
 * that recording an accepted revision as rejected would be a lie. So the picker,
 * which skipped only DISMISSED candidates, offered the first one again after it
 * had been kept. `openOffer` then compiled a spec identical to the accepted one
 * and `offer()` correctly declined it: *"already what they have. Silence is the
 * correct response to both."*
 *
 * Silence in the model, and on screen a suggestion button that does nothing.
 *
 * The dead button was not the cost. **The second candidate became unreachable**
 * — the one carrying `density: 'dense'` and a suppressed role, the one that
 * moves the surface toward something you work in rather than read. So the taste
 * model could only ever learn one thing, and task 0045 phase 3 asks precisely
 * whether taste alone can point the generator in two directions. It could not,
 * for this reason and not for any reason about taste.
 *
 * ## The rule
 *
 * Offer a candidate that has been neither dismissed nor already learned. A
 * suggestion that would change nothing is not a suggestion.
 */
import { candidateId, wasDismissed, type LensState } from '@morph/generation';
import type { TLayoutSpec } from '@morph/spec';
import type { TastePriors } from '@morph/generation';

export interface Candidate {
  readonly taste: TastePriors;
  /** What the layout would become, in the user's words. */
  readonly what: string;
  /** Why the model thinks so. Shown verbatim; never generated from numbers. */
  readonly why: string;
}

/** The id a dismissal is recorded under. Derived from `what`, because that is
 *  the part a person actually chose against. */
export const candidateKey = (c: Candidate): string => `cand-${c.what}`;

/**
 * Record a dismissal — the other end of `pickCandidate`'s filter, and it lives
 * here so the two cannot disagree about what an id is.
 *
 * **They did disagree, and the filter had never excluded anything.** `main.ts`
 * dismissed through `dismiss()` in `candidate.ts`, which records
 * `state.offered.id` — the content hash of the offered SPEC, correct for its
 * own job of recognising a spec arrived at twice. Then it handed
 * `state.lens.dismissed` to `pickCandidate`, which tests membership by
 * `cand-${what}`. Two id namespaces that never intersect, so pressing
 * "Not this" retired nothing: the same suggestion came back, `offer()`
 * correctly declined a spec whose id WAS in that list, and the button went
 * dead — with the second candidate unreachable again, by a second mechanism,
 * inside the module extracted to fix the first one.
 *
 * The unit test above it passed throughout, because it built its own argument
 * out of `candidateKey`. A test that mints the caller's input from the
 * module's own convention is a test that the module agrees with itself.
 *
 * Idempotent: dismissing twice is one entry, so a double-click cannot grow the
 * list.
 */
export function dismissCandidate(
  dismissed: readonly string[],
  c: Candidate,
): readonly string[] {
  const id = candidateKey(c);
  return dismissed.includes(id) ? dismissed : [...dismissed, id];
}

/**
 * Does the taste model already hold everything this candidate would teach?
 *
 * Compared by VALUE rather than by identity, because `suppressedRoles` is an
 * array: `['duration'] === ['duration']` is false, and a picker that used
 * identity would offer a candidate forever whose every prior was already held.
 */
export function alreadyHeld(c: Candidate, taste: TastePriors): boolean {
  return Object.entries(c.taste).every(([k, v]) =>
    JSON.stringify(taste[k as keyof TastePriors]) === JSON.stringify(v));
}

/** The next candidate worth offering, or `undefined` when there is none. */
export function pickCandidate(
  candidates: readonly Candidate[],
  dismissed: readonly string[],
  taste: TastePriors,
): Candidate | undefined {
  return candidates.find((c) =>
    !dismissed.includes(candidateKey(c)) && !alreadyHeld(c, taste));
}

/**
 * Why an explicit press produced no offer, or `undefined` when one appeared.
 *
 * `offer()` returns the lens UNCHANGED for two cases, and its own comment says
 * why: *"Already rejected, or already what they have. Silence is the correct
 * response to both."*
 *
 * That is right for an **ambient** offer — the model proposing unbidden should
 * not re-offer what you have — and wrong for a **press**. Found on the deployed
 * build by @Codex, #184: after Keeping a lens and then pressing *Suggest from
 * the pool*, *"nothing visibly happened. No pool offer appeared, no Keep button
 * appeared, and no failure notice or browser error was emitted."*
 *
 * A person who pushes a button has asked a question, and silence is the one
 * answer a button must never give. So the caller asks this and says the result.
 *
 * The two cases are separated because they are different sentences: one says
 * the pool agrees with you, the other says it is proposing something you have
 * already turned down. Collapsing them into "nothing to suggest" would make the
 * second unexplainable — press it again and it stays silent, with no way to
 * learn that a dismissal is what is in the way.
 */
export function refusedOffer(
  before: LensState, after: LensState, spec: TLayoutSpec,
  source: OfferSource = 'the pool',
): string | undefined {
  if (after !== before) return undefined;
  // Through the shared predicate, so this sentence and the silence it explains
  // cannot disagree about what was dismissed. It compared `candidateId(spec)`
  // against a list that now holds canonical text, and an id-vs-text membership
  // test is one that can only ever answer no.
  return wasDismissed(before, spec)
    ? `that layout was dismissed — nothing else in ${source} fits yet`
    : `${source} suggests the layout you already have`;
}

/**
 * WHERE the refused suggestion came from, because the sentence names it.
 *
 * @Codex, P2 on #184: *"This reuses `refusedOffer()` from the pool path, but
 * that helper only returns pool-specific messages. If an ordinary candidate
 * compiles to the already accepted spec … clicking the ordinary trigger now
 * reports 'the pool suggests the layout you already have' even though no pool
 * snapshot or read occurred."*
 *
 * Right, and worse than a wording slip: decision 0039 makes reading the pool an
 * explicit occasion, so a sentence claiming one happened when none did is the
 * product lying about a privacy boundary. Two words, not an enum of messages —
 * the two SHAPES of refusal are the same for both callers, and only the noun
 * differs.
 */
export type OfferSource = 'the pool' | 'Morph';
